import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../services/axios";
import MainBtn from "../../components/main/main_btn/MainBtn";
import { Button, Popconfirm, Modal, App, Grid } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import OnboardingWizard from "../../components/installer/OnboardingWizard";
import ConfigureWiFiModal from "../../components/device/ConfigureWiFiModal";
import { connect } from "react-redux";
import { mapDispatchToProps, mapStateToProps } from "../../store/dispatcher";
import { DispatchProps, StateProps } from "../../store";

type AssignedUser = {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
};

type AvailableRole = {
  name: string;
  description: string;
};

const UserManagement: React.FC<StateProps & DispatchProps> = ({ homes, devices, createDevice, fetchHomes }) => {
  const { t } = useTranslation("user_management");
  const { notification } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AssignedUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState<number[]>([]);
  const [selectedAvailableUsers, setSelectedAvailableUsers] = useState<number[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [assigning, setAssigning] = useState<boolean>(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [newUser, setNewUser] = useState({
    email: "",
    first_name: "",
    last_name: ""
  });
  const [creating, setCreating] = useState<boolean>(false);
  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [quickCreateModalOpen, setQuickCreateModalOpen] = useState<boolean>(false);
  const [dongleWiFiModalOpen, setDongleWiFiModalOpen] = useState<boolean>(false);

  const fetchAssigned = async () => {
    const res = await axios
      .get("/api-v1/users/my-assigned-users/")
      .then((e) => e.data)
      .catch(() => null);
    if (res) setAssignedUsers(res);
  };

  const fetchPendingRequests = async () => {
    const res = await axios
      .get("/api-v1/users/pending-assignment-requests/")
      .then((e) => e.data)
      .catch(() => null);
    if (res) setAvailableUsers(res);
  };

  const fetchAvailableRoles = async () => {
    const res = await axios
      .get("/api-v1/users/available-roles/")
      .then((e) => e.data)
      .catch(() => null);
    if (res) setAvailableRoles(res);
  };

  const approveAssignments = async () => {
    if (selectedAvailableUsers.length === 0) {
      setMessage({type: 'error', text: t('error_select_users_approve')});
      return;
    }

    setAssigning(true);
    try {
      // Approve each selected user
      const approvePromises = selectedAvailableUsers.map(user_id => 
        axios.post("/api-v1/users/approve-assignment/", { user_id })
      );
      await Promise.all(approvePromises);
      
      setMessage({type: 'success', text: t('success_approved_users', {count: selectedAvailableUsers.length})});
      setSelectedAvailableUsers([]);
      fetchAssigned();
      fetchPendingRequests();
    } catch (error: any) {
      setMessage({type: 'error', text: error.response?.data?.error || t('error_failed_approve')});
    }
    setAssigning(false);
  };

  const rejectAssignments = async () => {
    if (selectedAvailableUsers.length === 0) {
      setMessage({type: 'error', text: t('error_select_users_reject')});
      return;
    }

    setAssigning(true);
    try {
      // Reject each selected user
      const rejectPromises = selectedAvailableUsers.map(user_id => 
        axios.post("/api-v1/users/reject-assignment/", { user_id })
      );
      await Promise.all(rejectPromises);
      
      setMessage({type: 'success', text: t('success_rejected_users', {count: selectedAvailableUsers.length})});
      setSelectedAvailableUsers([]);
      fetchPendingRequests();
    } catch (error: any) {
      setMessage({type: 'error', text: error.response?.data?.error || t('error_failed_reject')});
    }
    setAssigning(false);
  };

  const unassignUsers = async () => {
    if (selectedAssignedUsers.length === 0) {
      setMessage({type: 'error', text: t('error_select_users_unassign')});
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post("/api-v1/users/unassign-from-me/", {
        user_ids: selectedAssignedUsers
      });
      
      setMessage({type: 'success', text: response.data.message});
      setSelectedAssignedUsers([]);
      fetchAssigned();
      fetchPendingRequests();
    } catch (error: any) {
      setMessage({type: 'error', text: error.response?.data?.error || t('error_failed_unassign')});
    }
    setAssigning(false);
  };

  const assignRole = async () => {
    if (selectedAssignedUsers.length === 0 || !selectedRole) {
      setMessage({type: 'error', text: t('error_select_users_and_role')});
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post("/api-v1/users/assign-role/", {
        user_ids: selectedAssignedUsers,
        role: selectedRole
      });
      
      setMessage({type: 'success', text: response.data.message});
      setSelectedAssignedUsers([]);
      setSelectedRole("");
      fetchAssigned(); // Refresh the list
    } catch (error: any) {
      setMessage({type: 'error', text: error.response?.data?.error || t('error_failed_assign_role')});
    }
    setAssigning(false);
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.first_name || !newUser.last_name) {
      notification.error({
        message: t('error_fill_all_fields'),
        placement: "topLeft",
      });
      return;
    }

    setCreating(true);
    try {
      const defaultPassword = "Soliseco1234!";
      
      const response = await axios.post("/api-v1/users/create-user/", {
        email: newUser.email,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        password: defaultPassword
      });
      
      notification.success({
        message: t('success_user_created'),
        description: response.data.message,
        placement: "topLeft",
      });
      
      setNewUser({
        email: "",
        first_name: "",
        last_name: ""
      });
      setQuickCreateModalOpen(false);
      fetchAssigned(); // Refresh the assigned users list
    } catch (error: any) {
      const errorMsg = error.response?.data?.username?.[0] || 
                       error.response?.data?.email?.[0] || 
                       error.response?.data?.error || 
                       t('error_failed_create_user');
      notification.error({
        message: t('error_create_user'),
        description: errorMsg,
        placement: "topLeft",
      });
    }
    setCreating(false);
  };

  const handleQuickCreateCancel = () => {
    setQuickCreateModalOpen(false);
    setNewUser({
      email: "",
      first_name: "",
      last_name: ""
    });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAssigned(), fetchPendingRequests(), fetchAvailableRoles()]).finally(() => {
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ 
      padding: '16px',
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#495057',
          marginBottom: '8px'
        }}>
          {t('loading_title')}
        </div>
        <div style={{
          fontSize: '14px',
          color: '#6c757d'
        }}>
          {t('loading_description')}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .user-management-container {
            padding: 12px !important;
          }
          .user-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .user-role-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .user-select {
            font-size: 16px !important; /* Prevents zoom on iOS */
          }
        }
        @media (max-width: 1200px) {
          .user-role-grid {
            grid-template-columns: 1fr 1fr !important;
            grid-template-rows: auto auto !important;
          }
          .user-role-grid > div:nth-child(1) {
            grid-column: 1 / 2 !important;
          }
          .user-role-grid > div:nth-child(2) {
            grid-column: 2 / 3 !important;
          }
          .user-role-grid > div:nth-child(3) {
            grid-column: 1 / 2 !important;
            grid-row: 2 / 3 !important;
          }
          .user-role-grid > div:nth-child(4) {
            grid-column: 2 / 3 !important;
            grid-row: 2 / 3 !important;
          }
        }
        @media (max-width: 768px) {
          .user-role-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto auto auto !important;
          }
          .user-role-grid > div:nth-child(1) {
            grid-column: 1 / 2 !important;
            grid-row: 1 / 2 !important;
          }
          .user-role-grid > div:nth-child(2) {
            grid-column: 1 / 2 !important;
            grid-row: 2 / 3 !important;
          }
          .user-role-grid > div:nth-child(3) {
            grid-column: 1 / 2 !important;
            grid-row: 3 / 4 !important;
          }
          .user-role-grid > div:nth-child(4) {
            grid-column: 1 / 2 !important;
            grid-row: 4 / 5 !important;
          }
        }
        @media (max-width: 480px) {
          .user-management-container {
            padding: 8px !important;
          }
          .user-card {
            padding: 16px !important;
          }
          .user-button {
            padding: 10px 14px !important;
            font-size: 13px !important;
          }
        }
        .header-with-button {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }
        @media (max-width: 768px) {
          .header-with-button {
            flex-direction: column;
            align-items: flex-start;
          }
          .header-with-button h2 {
            font-size: 20px !important;
          }
          .header-with-button .main-btn {
            width: 100%;
          }
        }
        .create-user-buttons-container {
          width: 100%;
          box-sizing: border-box;
        }
        .create-user-buttons-container > div {
          display: flex;
          box-sizing: border-box;
        }
        .create-user-buttons-container .main-btn {
          width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .create-user-buttons-container > div {
            minWidth: 0 !important;
            maxWidth: 100% !important;
            flex: 1 1 100% !important;
          }
          .create-user-buttons-container .main-btn {
            min-width: 0 !important;
            width: 100% !important;
          }
          .create-user-buttons-container {
            gap: 12px !important;
          }
        }
      `}</style>
      <div className="user-management-container" style={{ 
        padding: '16px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
      <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600' }}>{t('title')}</h2>
      
      {message && (
        <div style={{ 
          padding: 12, 
          marginBottom: 16, 
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: 4
        }}>
          {message.text}
          <button 
            onClick={() => setMessage(null)}
            style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Responsive Two Column Layout */}
      <div className="user-grid" style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Left Column - Pending Assignment Requests */}
        <div className="user-card" style={{ 
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '20px',
          border: '1px solid var(--clr-divider)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#495057'
          }}>
            {t('pending_assignment_requests')} ({availableUsers.length})
          </h3>
          <div style={{ marginBottom: 16 }}>
            <div style={{
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: 'white',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {availableUsers.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  {t('no_pending_requests')}
                </div>
              ) : (
                availableUsers.map(user => (
                  <div 
                    key={user.id}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #f1f3f4',
                      cursor: 'pointer',
                      backgroundColor: selectedAvailableUsers.includes(user.id) ? '#e3f2fd' : 'white',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => {
                      if (selectedAvailableUsers.includes(user.id)) {
                        setSelectedAvailableUsers(prev => prev.filter(id => id !== user.id));
                      } else {
                        setSelectedAvailableUsers(prev => [...prev, user.id]);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={selectedAvailableUsers.includes(user.id)}
                        onChange={() => {}} // Handled by parent div onClick
                        style={{ margin: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1976d2', marginBottom: '2px' }}>
                          {user.first_name} {user.last_name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>
                          {user.email}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {t('user_role_display', {role: user.role, username: user.username})}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <MainBtn
                title={assigning ? t('approving') : t('approve_selected')}
                onClick={approveAssignments}
                disabled={assigning || selectedAvailableUsers.length === 0}
                loading={assigning}
                color="primary"
                variant="outline"
              />
            </div>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <button
                onClick={rejectAssignments}
                disabled={assigning || selectedAvailableUsers.length === 0}
                className="main-btn color-danger variant-outline"
                style={{
                  backgroundColor: '#dc3545',
                  borderColor: '#dc3545',
                  color: 'white',
                  boxShadow: '2px 10px 34px 10px rgba(220, 53, 69, 0.12)',
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: (assigning || selectedAvailableUsers.length === 0) ? 0.6 : 1,
                  cursor: (assigning || selectedAvailableUsers.length === 0) ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!assigning && selectedAvailableUsers.length > 0) {
                    e.currentTarget.style.backgroundColor = '#c82333';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!assigning && selectedAvailableUsers.length > 0) {
                    e.currentTarget.style.backgroundColor = '#dc3545';
                  }
                }}
              >
                {assigning ? (
                  <div className="loader" style={{
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderLeftColor: 'transparent',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  <span>{t('reject_selected')}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Assigned Users */}
        <div className="user-card" style={{ 
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '20px',
          border: '1px solid var(--clr-divider)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#495057'
          }}>
            {t('my_assigned_users')} ({assignedUsers.length})
          </h3>
          <div style={{ marginBottom: 16 }}>
            <div style={{
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: 'white',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {assignedUsers.map(user => (
                <div 
                  key={user.id}
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid #f1f3f4',
                    cursor: 'pointer',
                    backgroundColor: selectedAssignedUsers.includes(user.id) ? '#e3f2fd' : 'white',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    if (selectedAssignedUsers.includes(user.id)) {
                      setSelectedAssignedUsers(prev => prev.filter(id => id !== user.id));
                    } else {
                      setSelectedAssignedUsers(prev => [...prev, user.id]);
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedAssignedUsers.includes(user.id)}
                      onChange={() => {}} // Handled by parent div onClick
                      style={{ margin: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#1976d2', marginBottom: '2px' }}>
                        {user.first_name} {user.last_name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>
                        {user.email}
                      </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {t('user_role_display', {role: user.role, username: user.username})}
                        </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <Popconfirm
            placement="topRight"
            title={t('unassign_selected')}
            description={t('unassign_selected') + ' - ' + selectedAssignedUsers.length + ' user(s)?'}
            okButtonProps={{ loading: assigning }}
            onConfirm={unassignUsers}
            disabled={assigning || selectedAssignedUsers.length === 0}
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
          >
            <Button 
              danger 
              disabled={assigning || selectedAssignedUsers.length === 0}
              loading={assigning}
              style={{ width: '100%' }}
            >
              {t('unassign_selected')}
            </Button>
          </Popconfirm>
        </div>
      </div>

      {/* Role Assignment Section */}
      <div style={{ 
        marginTop: '32px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '20px',
        border: '1px solid var(--clr-divider)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px', 
          fontWeight: '600',
          color: '#495057'
        }}>
        {t('assign_role_title')}
        </h3>
        
        <div className="user-role-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              {t('select_role_label')}
            </label>
            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="user-select"
              style={{ 
                width: '100%', 
                padding: '12px 16px',
                border: '1px solid var(--clr-divider)',
                borderRadius: '6px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            >
              <option value="">{t('choose_role_placeholder')}</option>
              {availableRoles.map(role => (
                <option key={role.name} value={role.name}>
                  {role.name} - {role.description}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <MainBtn
              title={assigning ? t('assigning_role') : t('assign_role_button')}
              onClick={assignRole}
              disabled={assigning || selectedAssignedUsers.length === 0 || !selectedRole}
              loading={assigning}
              color="primary"
              variant="outline"
            />
          </div>
        </div>
      </div>

      {/* Create New User Section */}
      <div style={{ 
        marginTop: '32px',
        backgroundColor: '#f8f9fa',
        padding: isMobile ? '16px' : '20px',
        borderRadius: '20px',
        border: '1px solid var(--clr-divider)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px', 
          fontWeight: '600',
          color: '#495057'
        }}>
          {t('create_new_user_title')}
        </h3>
        
        <div className="create-user-buttons-container" style={{ 
          display: 'flex',
          gap: isMobile ? '12px' : '16px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ 
            flex: isMobile ? '1 1 100%' : '1', 
            minWidth: isMobile ? '0' : '200px', 
            maxWidth: isMobile ? '100%' : '300px',
            boxSizing: 'border-box'
          }}>
            <MainBtn
              title={t('onboard_new_user_btn')}
              onClick={() => setWizardOpen(true)}
              color="primary"
            />
          </div>
          <div style={{ 
            flex: isMobile ? '1 1 100%' : '1', 
            minWidth: isMobile ? '0' : '200px', 
            maxWidth: isMobile ? '100%' : '300px',
            boxSizing: 'border-box'
          }}>
            <MainBtn
              title={t('quick_create_user_button')}
              onClick={() => setQuickCreateModalOpen(true)}
              color="primary"
              variant="outline"
            />
          </div>
        </div>
        
        <div style={{ 
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          fontSize: '13px',
          color: '#004085'
        }}>
          <strong>Note:</strong> {t('new_user_note')}
        </div>
      </div>

      {/* Soliseco Dongle Onboarding Section */}
      <div style={{ 
        marginTop: '32px',
        backgroundColor: '#f8f9fa',
        padding: isMobile ? '16px' : '20px',
        borderRadius: '20px',
        border: '1px solid var(--clr-divider)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px', 
          fontWeight: '600',
          color: '#495057'
        }}>
          {t('dongle_onboarding_title', 'Soliseco Dongle Onboarding')}
        </h3>
        
        <div className="create-user-buttons-container" style={{ 
          display: 'flex',
          gap: isMobile ? '12px' : '16px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ 
            flex: isMobile ? '1 1 100%' : '1', 
            minWidth: isMobile ? '0' : '200px', 
            maxWidth: isMobile ? '100%' : '300px',
            boxSizing: 'border-box'
          }}>
            <MainBtn
              title={t('setup_dongle_wifi_button', 'Setup Dongle WiFi')}
              onClick={() => setDongleWiFiModalOpen(true)}
              color="primary"
            />
          </div>
        </div>
        
        <div style={{ 
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          fontSize: '13px',
          color: '#004085'
        }}>
          <strong>Note:</strong> {t('dongle_onboarding_note', 'Use this to configure the WiFi settings for the Soliseco dongle during user onboarding.')}
        </div>
      </div>
      </div>
      
      <OnboardingWizard
        open={wizardOpen}
        close={() => {
          setWizardOpen(false);
          fetchAssigned();
        }}
      />

      <ConfigureWiFiModal
        open={dongleWiFiModalOpen}
        close={() => setDongleWiFiModalOpen(false)}
        titleKey="configure_dongle_wifi_modal_title"
        descriptionKey="configure_dongle_wifi_modal_description"
        descriptionOptions={{ homeName: "" }}
        wifiSsid="SolisEco"
      />

      <Modal
        title={t('quick_create_user_title')}
        open={quickCreateModalOpen}
        onCancel={handleQuickCreateCancel}
        footer={[
          <Button key="cancel" onClick={handleQuickCreateCancel} size={isMobile ? "middle" : "large"}>
            {t('cancel')}
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={creating}
            onClick={createUser}
            disabled={!newUser.email || !newUser.first_name || !newUser.last_name}
            size={isMobile ? "middle" : "large"}
          >
            {creating ? t('creating_user') : t('create_user_button')}
          </Button>
        ]}
        width={isMobile ? "100%" : 500}
        centered
        style={isMobile ? { top: 20, margin: 0 } : undefined}
        styles={isMobile ? { body: { padding: 16 } } : undefined}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              {t('email_label')}
            </label>
            <input 
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              placeholder={t('email_placeholder')}
              style={{ 
                width: '100%', 
                padding: '12px 16px',
                border: '1px solid var(--clr-divider)',
                borderRadius: '6px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              {t('first_name_label')}
            </label>
            <input 
              type="text"
              value={newUser.first_name}
              onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
              placeholder={t('first_name_placeholder')}
              style={{ 
                width: '100%', 
                padding: '12px 16px',
                border: '1px solid var(--clr-divider)',
                borderRadius: '6px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              {t('last_name_label')}
            </label>
            <input 
              type="text"
              value={newUser.last_name}
              onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
              placeholder={t('last_name_placeholder')}
              style={{ 
                width: '100%', 
                padding: '12px 16px',
                border: '1px solid var(--clr-divider)',
                borderRadius: '6px',
                fontSize: '16px',
                backgroundColor: 'white',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(UserManagement);
